import { Menu, UnstyledButton } from '@mantine/core';
import { useAtom } from 'jotai';

import { brand } from '../../branding';
import { DEFAULT_LOCALE, LOCALE_OPTIONS, markLocaleChosen, normalizeLocale, type CatalogLocale } from '../locale';
import { catalogLocaleAtom } from '../state';
import {
    HEADER_OVERLAY_ROW_HEIGHT,
    HEADER_OVERLAY_TOP_CSS,
    HEADER_OVERLAY_Z_INDEX,
    LANGUAGE_SWITCH_INSET,
    rightInset
} from './headerLayout';

// Language picker in the top-right of the header bar. Deliberately NOT a Mantine <Select> any more:
// under this app's @uag/doka-design theme that rendered as a full-width WHITE input field which
// ignored its own `w`/background props (the theme styles the inner <input> element, which a `w` on
// the Mantine root cannot reach) - it stretched to the right edge of the bar and covered the user
// avatar the blueprint chrome renders there (see chat: "Im Header ist das Benutzericon verloren
// gegangen ... Eventuell ist es von der Sprachauswahl überlagert").
//
// So the trigger is our own button and every visual property sits in an inline style, which beats
// any stylesheet rule: transparent (the bar's own yellow shows through, see chat: "in der
// Hintergrundfarbe von DOKA ... gelb, nicht grau"), a thin border and a chevron so it is
// unmistakably a picker ("Es muss klar sein, dass es eine Auswahlbox ist (caret nach unten)"), and
// the short codes DE/EN, which keep it narrow enough to leave the avatar alone.
//
// Colours follow the configured brand (see branding.ts), so a non-DOKA header bar stays legible.

/** Width of the trigger - fixed, so the header layout can't be pushed around by the label. */
const TRIGGER_WIDTH = 74;
const TRIGGER_HEIGHT = 30;
/** Above the header overlays themselves, so the open dropdown can never paint behind the bar. */
const DROPDOWN_Z_INDEX = HEADER_OVERLAY_Z_INDEX + 100;

const useLocaleChoice = (): [CatalogLocale, (value: CatalogLocale) => void] => {
    const [locale, setLocale] = useAtom(catalogLocaleAtom);
    const choose = (value: CatalogLocale) => {
        setLocale(value);
        // From here on the user's own choice outranks the language from the token (see
        // UserLocaleSync) - hence the explicit marker, not just the persisted value.
        markLocaleChosen();
    };
    return [normalizeLocale(locale) ?? DEFAULT_LOCALE, choose];
};

/** DE / EN plus a caret - `color` is passed in because the mobile bar is white, the header bar is not. */
const LanguageMenu = ({ color, compact = false }: { color: string; compact?: boolean }) => {
    const [locale, choose] = useLocaleChoice();

    return (
        <Menu position="bottom-end" radius="md" shadow="md" width={compact ? 120 : 150} zIndex={DROPDOWN_Z_INDEX}>
            <Menu.Target>
                <UnstyledButton
                    aria-label="Sprache / Language"
                    className="language-switch-trigger"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        width: compact ? 66 : TRIGGER_WIDTH,
                        height: compact ? 26 : TRIGGER_HEIGHT,
                        padding: '0 6px 0 10px',
                        // Transparent, not white: whatever colour the header bar has is the
                        // background of this control (DOKA yellow today).
                        backgroundColor: 'transparent',
                        border: `1px solid ${color}`,
                        borderRadius: 6,
                        color,
                        fontWeight: 700,
                        fontSize: compact ? 12 : 14,
                        lineHeight: 1
                    }}
                    title="Sprache / Language"
                >
                    <span>{locale.toUpperCase()}</span>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }} aria-hidden>
                        expand_more
                    </span>
                </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
                {LOCALE_OPTIONS.map((option) => (
                    <Menu.Item
                        key={option.value}
                        // The full language name stays as the accessible name and the tooltip - the
                        // list itself shows the codes the customer asked for.
                        aria-label={option.label}
                        fw={option.value === locale ? 700 : 400}
                        leftSection={
                            <span
                                className="material-symbols-outlined"
                                style={{ fontSize: 18, opacity: option.value === locale ? 1 : 0 }}
                                aria-hidden
                            >
                                check
                            </span>
                        }
                        title={option.label}
                        onClick={() => choose(option.value)}
                    >
                        {option.value.toUpperCase()}
                    </Menu.Item>
                ))}
            </Menu.Dropdown>
        </Menu>
    );
};

export const LanguageSwitch = () => (
    <div
        // Hidden below `md` - same reasoning as HeaderBrand/HeaderWishlist. LanguageSelectCompact
        // below is the mobile stand-in, rendered by MobileHeaderBar.
        className="hidden items-center md:flex"
        style={{
            position: 'fixed',
            top: HEADER_OVERLAY_TOP_CSS,
            // See headerLayout.rightInset - `right:` alone put this off the right edge of the screen.
            left: rightInset(LANGUAGE_SWITCH_INSET),
            width: TRIGGER_WIDTH,
            height: HEADER_OVERLAY_ROW_HEIGHT,
            zIndex: HEADER_OVERLAY_Z_INDEX
        }}
    >
        <LanguageMenu color={brand.headerForeground} />
    </div>
);

/** Narrow variant for MobileHeaderBar - that bar is white, so it keeps DOKA blue whatever the brand. */
export const LanguageSelectCompact = () => <LanguageMenu color={brand.primary} compact />;
